import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext
} from 'react'

import { createApiClient, createRestClient } from './../api'
import { useError } from './util'

import Spinner from '../components/Spinner'
import { auth, db } from '../firebase'
import { onAuthStateChanged } from 'firebase/auth'
import { useLocation, useNavigate } from 'react-router-dom'
import { doc, getDoc } from 'firebase/firestore'
import { createSocketClient } from '../services/socketService'

export const ApiContext = React.createContext()

export function ApiProvider({ children }) {
  const [state, setState] = useState({
    client: null,
    rest: null,
    socket: null,
    token: null,
    user: null,
    authUser: null,
    authLoading: true,
    profileLoading: false
  })

  const navigate = useNavigate()
  const location = useLocation()
  const setError = useError()
  const initialCheckDone = useRef(false)
  const mfaInProgress = localStorage.getItem('mfaInProgress') === 'true'
  const socketClientRef = useRef(null)

  const isPublicRoute = (pathname) => {
    const publicPaths = [
      '/login',
      '/signup',
      '/forgot-password',
      '/mfa-setup',
      '/verify-otp',
      '/reset-authentication',
      '/reset-password'
    ]
    return publicPaths.includes(pathname) || pathname.startsWith('/invite')
  }

  const cleanupSocket = useCallback(() => {
    if (socketClientRef.current) {
      socketClientRef.current.disconnect()
      socketClientRef.current = null
    }
  }, [])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      // Clean up previous socket connection
      cleanupSocket()

      setState((prev) => ({ ...prev, authUser, authLoading: false }))

      if (authUser) {
        if (mfaInProgress) {
          setState((prev) => ({
            ...prev,
            client: null,
            rest: null,
            socket: null,
            token: null,
            user: null,
            profileLoading: false
          }))
          return
        }

        try {
          setState((prev) => ({ ...prev, profileLoading: true }))

          const token = await authUser.getIdToken()
          const client = createApiClient(token)
          const endpoint = `users/${authUser.uid}`
          const response = await client(endpoint, null, 'GET')
          const rest = createRestClient(token)

          if (!response.fields) {
            throw new Error('No matching user in Firestore')
          }

          const firestoreUser = {
            id: authUser.uid,
            email: response.fields.email?.stringValue,
            isAdmin: response.fields.isAdmin?.booleanValue,
            isApproved: response.fields.isApproved?.booleanValue,
            isTwoFactorEnabled:
              response.fields.isTwoFactorEnabled?.booleanValue,
            dutyStatus: response.fields.dutyStatus?.booleanValue,
            displayName: response.fields.displayName?.stringValue,
            firstName: response.fields.firstName?.stringValue,
            lastName: response.fields.lastName?.stringValue,
            profileUrl: response.fields.profileUrl?.stringValue,
            chatNotification: response.fields.chatNotification?.booleanValue,
            showLastName: response.fields.showLastName?.booleanValue,
            userType: response?.fields?.userType?.mapValue.fields,
            organization:
              response?.fields?.organization?.mapValue?.fields?.name
                ?.stringValue,
            orgId:
              response?.fields?.organization?.mapValue?.fields?.id?.integerValue
          }

          // Create and connect socket
          const socketClient = createSocketClient()
          const socket = socketClient.connect()
          socketClientRef.current = socketClient

          setState({
            client,
            rest,
            socket,
            token,
            user: firestoreUser,
            authUser,
            authLoading: false,
            profileLoading: false
          })

          if (!initialCheckDone.current) {
            const redirectTo = location.state?.from?.pathname
            navigate(redirectTo || '/dispatch', { replace: true })
            initialCheckDone.current = true
          }
        } catch (err) {
          setError(err)
          setState({
            client: null,
            rest: null,
            socket: null,
            token: null,
            user: null,
            authUser,
            authLoading: false,
            profileLoading: false
          })

          if (!initialCheckDone.current && !isPublicRoute(location.pathname)) {
            navigate('/login', { replace: true })
            initialCheckDone.current = true
          }
        }
      } else {
        setState({
          client: null,
          rest: null,
          socket: null,
          token: null,
          user: null,
          authUser: null,
          authLoading: false,
          profileLoading: false
        })

        if (!initialCheckDone.current && !isPublicRoute(location.pathname)) {
          navigate('/login', { replace: true, state: { from: location } })
          initialCheckDone.current = true
        }
      }
    })

    return () => {
      unsubscribe()
      cleanupSocket()
    }
  }, [setError, navigate, location, mfaInProgress, cleanupSocket])

  if (state.authLoading || state.profileLoading) {
    return <Spinner />
  }

  return <ApiContext.Provider value={state}>{children}</ApiContext.Provider>
}

export function useApi() {
  const { client } = useContext(ApiContext)
  return [client]
}

export function useApiToken() {
  const { token } = useContext(ApiContext)
  return token
}

export function useRest() {
  const { rest } = useContext(ApiContext)
  return rest
}

export function useSocket() {
  const { socket } = useContext(ApiContext)
  return socket
}

export function useApiRequest(endpoint, payload, dependencies) {
  const [client] = useApi()

  const [data, setData] = useState(null)
  const setError = useError()

  const fetching = useRef(false)

  const fetchData = useCallback(
    async (isMounted) => {
      if (fetching.current || !client) {
        return
      }

      fetching.current = true

      try {
        const response = await client(endpoint, payload)
        fetching.current = false
        if (isMounted) {
          setData(response.fields || response)
        }
      } catch (err) {
        fetching.current = false
        if (isMounted) {
          setError(err)
        }
      }
    },
    [client, endpoint, payload, setError]
  )

  useEffect(() => {
    let isMounted = true
    if (dependencies) {
      for (const dependency of dependencies) {
        if (!dependency) {
          return
        }
      }
    }

    if (data) {
      return
    }

    fetchData(isMounted)
    return () => (isMounted = false)
  })

  return [data, () => fetchData(true)]
}

export function usePagination(opts) {
  opts = opts || {}
  const localStorageKey = `pagination-${opts.uid}`

  const [client] = useApi()

  if (localStorage[localStorageKey]) {
    try {
      opts = {
        ...opts,
        ...JSON.parse(localStorage[localStorageKey])
      }
    } catch (err) {
      console.error(err)
    }
  }

  const [state, setState] = useState({
    currentPage: opts.currentPage || 0,
    itemsPerPage: opts.itemsPerPage,
    sortBy: opts.sortBy || 'id',
    sortOrder: opts.sortOrder || 'desc',
    persistent: opts.persistent,
    totalCount: 0,
    items: null
  })
  const [loading, setLoading] = useState(false)

  const setError = useError()
  const fetching = useRef(false)

  const serialize = useCallback(
    (opts) => {
      localStorage[localStorageKey] = JSON.stringify({
        sortBy: opts.sortBy,
        sortOrder: opts.sortOrder,
        currentPage: opts.currentPage,
        itemsPerPage: opts.itemsPerPage
      })
    },
    [localStorageKey]
  )

  const fetchItems = useCallback(
    async (pageIndex) => {
      if (fetching.current) {
        return
      }

      fetching.current = true
      setLoading(true)

      const params = new URLSearchParams({
        offset: pageIndex * state.itemsPerPage,
        limit: state.itemsPerPage,
        sortBy: state.sortBy,
        sortOrder: state.sortOrder
      }).toString()

      let response
      try {
        response = await client(`${opts.endpoint}?${params}`)
      } catch (err) {
        setError(err)
        setLoading(false)
        return
      }

      fetching.current = false

      const numPages = Math.ceil(response.count / state.itemsPerPage)
      if (pageIndex > numPages - 1) {
        delete localStorage[localStorageKey]
      }

      const newState = {
        ...state,
        currentPage: pageIndex,
        totalCount: response.count,
        items: response.items
      }

      if (opts.persistent) {
        serialize(newState)
      }

      setState(newState)
      setLoading(false)
    },
    [
      client,
      state,
      setState,
      setError,
      opts.endpoint,
      opts.persistent,
      serialize,
      localStorageKey
    ]
  )

  const oldStateRef = useRef(state)

  useEffect(
    function () {
      const oldState = oldStateRef.current
      if (
        state.items === null ||
        oldState.sortBy !== state.sortBy ||
        oldState.sortOrder !== state.sortOrder ||
        oldState.itemsPerPage !== state.itemsPerPage
      ) {
        fetchItems(state.currentPage)
      }

      oldStateRef.current = state
    },
    [fetchItems, state]
  )

  function setOpts(opts) {
    if (opts.persistent) {
      serialize(opts)
    }

    if (opts.itemsPerPage !== oldStateRef.current.itemsPerPage) {
      opts.currentPage = 0
    }

    setState(opts)
  }

  return [
    state.items,
    state.currentPage,
    state.totalCount,
    loading,
    fetchItems,
    setOpts,
    state
  ]
}

export function usePermissions(resource, actionWithPossession) {
  const [permission, setPermission] = useState({ granted: false })

  useEffect(() => {
    async function checkPermissions() {
      const user = auth.currentUser
      if (!user) {
        setPermission({ granted: false })
        return
      }

      const match = actionWithPossession.match(
        /(create|read|update|delete)(Any|Own)?/i
      )
      const action = match?.[1] || actionWithPossession
      const possession = match?.[2]?.toLowerCase() || 'any'

      const userRef = doc(db, 'users', user.uid)
      const userSnap = await getDoc(userRef)

      if (!userSnap.exists()) {
        setPermission({ granted: false })
        return
      }

      const userData = userSnap.data()
      const orgId = userData.organization?.id?.toString()
      const roleBindings = userData.roleBindings || []
      const admin = userData?.isAdmin || false

      let granted = false
      if (admin) {
        granted = true
      } else {
      for (const binding of roleBindings) {
        const roleId = binding.role?.id
        if (!roleId) continue

        const roleRef = doc(db, 'organizations', orgId, 'user-roles', roleId)
        const roleSnap = await getDoc(roleRef)

        if (roleSnap.exists()) {
          const grants = roleSnap.data().grants || []

          if (
            grants.some(
              (g) =>
                g.resource === resource &&
                g.action === action &&
                g.possession.toLowerCase() === possession
            )
          ) {
            granted = true
            break
          }
        }
      }}

      setPermission({ granted })
    }

    checkPermissions()
  }, [resource, actionWithPossession])

  return permission
}

export const MFA_SESSION_EXPIRED =
  'Your sign-in has expired. Please sign in again and restart the reset.'

// Both MFA reset endpoints require a Firebase ID token and only act on the
// account that token belongs to. The environment is part of the function name
// now — `isProd` in the body is rejected with a 400.
const callMfaResetEndpoint = async (name, data) => {
  const user = auth.currentUser
  if (!user) {
    throw new Error(MFA_SESSION_EXPIRED)
  }

  const idToken = await user.getIdToken()
  const suffix = process.env.REACT_APP_ENV === 'production' ? 'P' : 'S'

  const response = await fetch(
    `https://us-central1-live-alarm-v2.cloudfunctions.net/${name}${suffix}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`
      },
      body: JSON.stringify(data)
    }
  )

  const body = await response.json().catch(() => ({}))

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error(MFA_SESSION_EXPIRED)
    }
    // 403 and 429 carry a message meant for the user.
    throw new Error(body.message || `HTTP error! status: ${response.status}`)
  }

  return body
}

export const sendEmail = (data) =>
  callMfaResetEndpoint('sendResetMFAVerification', data)

export const varifyMfaReset = (data) => callMfaResetEndpoint('verifyMFAOtp', data)

// Phase 4A (T22, T23). The second factor is checked by the server now: the
// code used to be compared in the browser, so the Firebase ID token carried no
// evidence the step happened (Appendix A 1.4). Recovery codes go through the
// same call, otherwise recovery would simply become the new way to skip it.
//
// getIdToken(true) forces a refresh so the new token carries the `mfa` claim
// the function just wrote. Without it the claim stays invisible for up to an
// hour.
export const verifyTotp = async ({ code, isRecoveryCode = false }) => {
  const body = await callMfaResetEndpoint('verifyTotp', { code, isRecoveryCode })
  await auth.currentUser?.getIdToken(true)
  return body
}

export function useIncidentSocket(incidentId, onTrackUpdate) {
  const socket = useSocket()

  useEffect(() => {
    if (!socket || !incidentId) return

    socket.emit('join_incident', incidentId)

    const handler = (payload) => {
      onTrackUpdate?.(payload)
    }

    socket.on('track_update', handler)

    return () => {
      socket.off('track_update', handler)
      socket.emit('leave_incident', incidentId)
    }
  }, [socket, incidentId, onTrackUpdate])
}
